import { useState } from 'react';
import { IMPORT_DAY_OPTIONS, type AdminAiStatus, type AdminHistory, type AiStart, type ImportDays } from '@coachrex/shared';
import { Link, useParams } from 'react-router-dom';
import { useAdminAi, useAdminCompany, useSetImportDays, useStartHistory, useSetAi, useSetAiStart } from '../lib/api';
import { Chip, ErrorBox, Skeleton } from '../components/ui';

const usd = (n: number) => `$${n.toFixed(n < 10 ? 2 : 0)}`;

export function CompanyDetail() {
  const { id = '' } = useParams();
  const ai = useAdminAi(id);
  const setAi = useSetAi(id);

  const s = ai.data;
  return (
    <div className="page" style={{ maxWidth: 820 }}>
      <ImportWindow id={id} />
      <section className="wiz-card" style={{ gap: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="wiz-title" style={{ flex: 1 }}>
            <b>AI analysis</b>
            <span>Call summaries and scores, objection tags and A–D lead grades. Runs on new activity from the moment it's switched on, or from a start you pick below.</span>
          </div>
          {s && (
            <button
              className={`toggle${s.enabled ? ' is-on' : ''}`}
              role="switch"
              aria-checked={s.enabled}
              aria-label="AI analysis"
              disabled={!s.available || setAi.isPending}
              onClick={() => setAi.mutate(!s.enabled)}
            />
          )}
        </div>

        {ai.error ? (
          <ErrorBox error={ai.error} />
        ) : !s ? (
          <Skeleton height={120} />
        ) : !s.available ? (
          <div className="test-err">The server has no Anthropic API key. Add ANTHROPIC_API_KEY as a GitHub repository secret and re-run the Provision workflow.</div>
        ) : (
          <>
            {setAi.error && <ErrorBox error={setAi.error} />}
            {s.lastError && (
              <div className="test-err">
                Last error {new Date(s.lastError.at).toLocaleString()}: {s.lastError.message}
              </div>
            )}
            <div className="review-row">
              <span>Status</span>
              <span>{s.enabled ? <Chip tone="green">On since {new Date(s.enabledAt!).toLocaleDateString()}</Chip> : <Chip tone="grey">Off</Chip>}</span>
            </div>
            <StartFrom id={id} s={s} />
            <div className="review-row">
              <span>Model</span>
              <span>{s.model}</span>
            </div>
            <div className="review-row">
              <span>Spend this month</span>
              <span>
                {usd(s.monthSpendUsd)} of {usd(s.monthCapUsd)} cap
                <span className="help" style={{ display: 'block', fontWeight: 400 }}>
                  Analysis pauses for the rest of the month when the cap is reached.
                </span>
              </span>
            </div>
            <div className="review-row">
              <span>Last 30 days</span>
              <span>
                {s.callsAnalyzed30d.toLocaleString('en-US')} calls analyzed · {s.leadsGraded30d.toLocaleString('en-US')} leads graded
              </span>
            </div>
            <div className="review-row">
              <span>Call transcripts</span>
              <span>
                {s.transcripts.ready} received · {s.transcripts.waiting} waiting · {s.transcripts.none} unavailable
                <span className="help" style={{ display: 'block', fontWeight: 400 }}>
                  {s.transcription === 'deepgram'
                    ? 'Transcribed by Deepgram from the call recordings (about $0.0043 per minute, counted in the spend above).'
                    : s.enabled && s.transcripts.ready === 0 && s.transcripts.none > 0
                      ? "HighLevel isn't returning transcripts. Add a Deepgram key, or turn on call transcription in this sub-account."
                      : "Using HighLevel's call transcripts."}
                </span>
              </span>
            </div>

            {s.enabled && <History id={id} h={s.history} />}
          </>
        )}
      </section>
    </div>
  );
}

const START_LABEL: Record<AiStart, string> = { today: 'Today', days14: 'Last 2 weeks', days30: 'Last 30 days' };

/** Where live analysis starts: today, or a couple of weeks back to catch up on recent calls. */
function StartFrom({ id, s }: { id: string; s: AdminAiStatus }) {
  const set = useSetAiStart(id);
  const [pick, setPick] = useState<AiStart | null>(null);
  const chosen = s.start.options.find((o) => o.key === pick);
  const day = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const current = s.enabled ? s.start.options.find((o) => Math.abs(Date.parse(o.from) - Date.parse(s.enabledAt!)) < 60_000)?.key : undefined;
  return (
    <div className="review-row">
      <span>Analyze calls from</span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span>{s.enabled ? `${new Date(s.enabledAt!).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} onwards` : 'Not started'}</span>
        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {s.start.options.map((o) => (
            <button
              key={o.key}
              className={`btn btn--sm ${pick === o.key || (!pick && current === o.key) ? 'btn--primary' : 'btn--secondary'}`}
              disabled={!!s.start.blocked || set.isPending}
              onClick={() => setPick(o.key)}
            >
              {START_LABEL[o.key]}
            </button>
          ))}
        </span>
        {s.start.blocked && <span className="help">{s.start.blocked}</span>}
        {chosen && !s.start.blocked && (
          <div className="test-ok" style={{ background: 'var(--amber-bg)' }}>
            <span style={{ fontSize: 13, color: 'var(--body)' }}>
              {s.enabled && Date.parse(chosen.from) >= Date.parse(s.enabledAt!)
                ? `Analyze only calls from ${day(chosen.from)} onwards? Calls already analyzed keep their summaries; nothing older is picked up.`
                : chosen.calls + chosen.leads === 0
                  ? `Analyze calls from ${day(chosen.from)} onwards? Nothing in that stretch is waiting, so there's no catch-up cost.`
                  : `Analyze calls from ${day(chosen.from)} onwards? Catches up on ${chosen.calls.toLocaleString('en-US')} calls and ${chosen.leads.toLocaleString('en-US')} leads for about ${usd(chosen.usd)}, counted in the monthly cap. Anything already analyzed isn't paid for again.`}
            </span>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn--primary btn--sm" disabled={set.isPending} onClick={() => set.mutate(chosen.key, { onSettled: () => setPick(null) })}>
                {set.isPending ? 'Saving…' : s.enabled ? 'Change start' : 'Turn on'}
              </button>
              <button className="btn btn--secondary btn--sm" onClick={() => setPick(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {set.error && <ErrorBox error={set.error} />}
      </span>
    </div>
  );
}

const STAGES: Record<NonNullable<AdminHistory['stage']>, string> = {
  importing: 'Importing older conversations from HighLevel',
  transcribing: 'Transcribing older calls',
  analyzing: 'Scoring older calls (half-price batch)',
  grading: 'Re-grading leads with the full history (half-price batch)',
  done: 'Done',
};

const span = (days: number) => (days >= 365 ? `${Math.round(days / 365)} year${days >= 730 ? 's' : ''}` : `${Math.round(days / 30)} months`);

/** Older history: import, transcribe, score calls and grade leads, with the cost approved up front. Can go further back later. */
function History({ id, h }: { id: string; h: AdminHistory }) {
  const start = useStartHistory(id);
  const [confirm, setConfirm] = useState(false);
  const [pick, setPick] = useState<number | null>(null);
  const n = (x: number) => x.toLocaleString('en-US');
  const running = h.stage && h.stage !== 'done';
  const chosen = h.options.find((o) => o.days === pick) ?? h.options[0];
  return (
    <div className="field" style={{ gap: 10, paddingTop: 4 }}>
      <span className="field-label">History{h.stage === 'done' ? `: ${span(h.days)} analyzed` : ''}</span>
      {h.stage && (
        <span className="help" style={{ marginTop: -6 }}>
          {STAGES[h.stage]}
          {h.stage === 'importing' && h.importProgress !== null && ` · ${h.importProgress}%`}
          {h.stage === 'transcribing' && ` · ${n(h.transcripts.ready)} done, ${n(h.transcripts.waiting)} to go`}
          {` · ${usd(h.spentUsd)} spent${running ? ' so far' : ''}`}
        </span>
      )}
      {!running && chosen && (
        <>
          <span className="help" style={{ marginTop: h.stage ? 0 : -6 }}>
            {h.stage
              ? 'Go further back: imports the older conversations, then transcribes, scores and grades only what wasn’t analyzed before. Nothing is paid for twice.'
              : `Imports HighLevel history, transcribes the older calls, scores them and grades the leads, so reports, Ask Lex and AI milestones cover it.`}{' '}
            Runs in the background over a few hours and is kept out of the monthly cap.
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <select className="select-pill" aria-label="How far back" value={chosen.days} onChange={(e) => setPick(Number(e.target.value))}>
              {h.options.map((o) => (
                <option key={o.days} value={o.days}>
                  Back {span(o.days)} · about {usd(o.usd)}
                </option>
              ))}
            </select>
            {!confirm && (
              <button className="btn btn--secondary" onClick={() => setConfirm(true)}>
                Import and analyze
              </button>
            )}
          </div>
          <span className="help">Rough estimate from the call volume already imported; older months may have had more or fewer calls.</span>
          {confirm && (
            <div className="test-ok" style={{ background: 'var(--amber-bg)' }}>
              <span style={{ fontSize: 13, color: 'var(--body)' }}>
                Go back {span(chosen.days)} for roughly {usd(chosen.usd)}?
              </span>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn--primary btn--sm" disabled={start.isPending} onClick={() => start.mutate(chosen.days, { onSettled: () => setConfirm(false) })}>
                  {start.isPending ? 'Starting…' : 'Start'}
                </button>
                <button className="btn btn--secondary btn--sm" onClick={() => setConfirm(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {start.error && <ErrorBox error={start.error} />}
    </div>
  );
}

/** How far back this company's import reads from HighLevel. */
function ImportWindow({ id }: { id: string }) {
  const company = useAdminCompany(id);
  const save = useSetImportDays(id);
  const days = company.data?.importDays;
  return (
    <section className="wiz-card" style={{ gap: 10 }}>
      <div className="wiz-title">
        <b>History to import</b>
        <span>How far back Coach Lex reads calls, texts, emails and tags from HighLevel. Shortening it while the first import runs stops it at the new start. Today reads only from midnight onward; anything already imported stays.</span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {IMPORT_DAY_OPTIONS.map((o) => (
          <button
            key={o.days}
            className={`btn btn--sm ${days === o.days ? 'btn--primary' : 'btn--secondary'}`}
            disabled={!company.data || save.isPending || days === o.days}
            onClick={() => save.mutate(o.days as ImportDays)}
          >
            {o.label}
          </button>
        ))}
      </div>
      {save.error && <ErrorBox error={save.error} />}
    </section>
  );
}
